import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface DataContextType {
  data: any[];
  requestsData: any[]; // For GroupSeriesReport
  loading: boolean;
  error: string | null;
  loadingProgress: string;
  refreshData: () => Promise<void>;
  cacheStatus: {
    isExpired: boolean;
    timeSinceLastFetch: number;
    nextRefreshIn: number;
  };
  checkAndRefreshCache: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<any[]>([]);
  const [requestsData, setRequestsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState("");
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
  const fetching = useRef(false);

  // Initialize lastFetchTime from localStorage if present
  useEffect(() => {
    const stored = localStorage.getItem('lastFetchTime');
    if (stored) {
      setLastFetchTime(Number(stored));
    }
  }, []);

  const fetchData = async (forceRefresh = false) => {
    if (fetching.current) return;
    fetching.current = true;
    try {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    
    // Check if cache is still valid (unless force refresh)
    if (!forceRefresh && lastFetchTime > 0 && timeSinceLastFetch < CACHE_DURATION) {
      console.log(`Using cached data (${Math.round(timeSinceLastFetch / 1000 / 60)} minutes old)`);
      return;
    }

    setLoading(true);
    setError(null);
      let allData: any[] = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      // Get total count first
      const { count: totalCount } = await supabase
        .from("mv_group_enriched_summary")
        .select("*", { count: "exact", head: true });

      while (hasMore) {
        const { data: groupData, error: groupError } = await supabase
          .from("mv_group_enriched_summary")
          .select("*")
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (groupError) {
          setError(groupError.message);
          return;
        }

        if (!groupData || groupData.length === 0) {
          hasMore = false;
        } else {
          allData = [...allData, ...groupData];
          page++;
          if (totalCount) {
            const percent = Math.round((allData.length / totalCount) * 100);
            setLoadingProgress(`Loading data: ${percent}%`);
          } else {
            setLoadingProgress(`Loading data: ~${allData.length} rows loaded...`);
          }
        }
      }

      setData(allData);
      setLoadingProgress("");

      // Also fetch requests data for GroupSeriesReport
      const { data: requestsData, error: requestsError } = await supabase
        .from("mv_requests_summary")
        .select("*")
        .eq('request_type', 'group_series')
        .eq('status', 'Open')
        .order('checkin', { ascending: false });

      if (requestsError) {
        console.warn('Failed to load requests data:', requestsError.message);
      } else {
        setRequestsData(requestsData || []);
      }

      // Update last fetch time
      setLastFetchTime(now);
      localStorage.setItem('lastFetchTime', String(now));
      console.log(`Data refreshed at ${new Date(now).toLocaleTimeString()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      fetching.current = false;
    }
  };

  const refreshData = async () => {
    await fetchData(true); // Force refresh
  };

  // Calculate cache status
  const cacheStatus = {
    isExpired: lastFetchTime > 0 && (Date.now() - lastFetchTime) >= CACHE_DURATION,
    timeSinceLastFetch: lastFetchTime > 0 ? Date.now() - lastFetchTime : 0,
    nextRefreshIn: lastFetchTime > 0 ? Math.max(0, CACHE_DURATION - (Date.now() - lastFetchTime)) : 0
  };

  // Function to check and refresh cache if needed
  const checkAndRefreshCache = async () => {
    if (fetching.current) return;
    if (cacheStatus.isExpired) {
      console.log('Cache expired, refreshing data...');
      await fetchData();
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <DataContext.Provider value={{ 
      data, 
      requestsData, 
      loading, 
      error, 
      loadingProgress, 
      refreshData, 
      cacheStatus, 
      checkAndRefreshCache 
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
} 